import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash navigation on page load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location]);

  const scrollToSection = (id, isHash) => {
    if (!isHash) {
      // It's a route, navigate using react-router
      navigate(id);
      setMobileMenuOpen(false);
      return;
    }

    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      setMobileMenuOpen(false);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setMobileMenuOpen(false);
      }
    }
  };

  const handleCartClick = () => {
    // Dispatch custom event to open cart
    window.dispatchEvent(new CustomEvent('openCart'));
  };

  const navItems = [
  { label: 'How It Works', id: 'how-it-works', isHash: true },
  { label: 'Services', id: 'services', isHash: true },
  { label: 'Blog', id: '/blog', isHash: false },
  { label: 'FAQ', id: 'faq', isHash: true }];


  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ?
      'bg-gray-900/98 backdrop-blur-md shadow-2xl' :
      'bg-gray-900/95 backdrop-blur-sm'}`
      }>

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and Brand Text */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}>

            {/* Logo Thumbnail */}
            <img
              src="https://customer-assets.emergentagent.com/job_5fbc50c9-4bd7-40ac-b8aa-d9e061478b31/artifacts/qa08h6b1_1a7271e6-e3a4-4b58-8ef7-af38b39095b4.png"
              alt="Apply Like a Pro Logo"
              className="h-12 w-12 object-contain" />

            
            {/* Text Content */}
            <div className="flex flex-col">
              <h1 className="font-serif-display md:text-2xl !text-xl text-white">

              </h1>
              <p className="text-xs md:text-sm text-gray-400 -mt-0.5">
                your job application concierge
              </p>
            </div>
          </motion.div>

          {/* Desktop Horizontal Menu */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden md:flex items-center space-x-2">

            {navItems.map((item, index) =>
            <motion.button
              key={item.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection(item.id, item.isHash)}
              className="px-4 py-2 text-teal-400 hover:text-teal-300 font-medium transition-colors rounded-lg hover:bg-white/10">

                {item.label}
              </motion.button>
            )}
            
            {/* Cart Button in Menu */}
            <motion.button
              data-testid="header-cart-button"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCartClick}
              className="relative px-4 py-2 text-teal-400 hover:text-teal-300 font-medium transition-colors rounded-lg hover:bg-white/10 flex items-center">

              <ShoppingCart size={20} />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-warm-coral-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {cartItems.length}
                </span>
              )}
            </motion.button>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}>

              <Button
                onClick={() => scrollToSection('services', true)}
                className="ml-2 bg-gradient-to-r from-warm-coral-500 to-warm-coral-600 hover:from-warm-coral-600 hover:to-warm-coral-700 text-white px-6 py-2 rounded-full shadow-lg transition-all">

                Get Started
              </Button>
            </motion.div>
          </motion.div>

          {/* Mobile Hamburger Menu Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:hidden">

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors">

              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </motion.div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen &&
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10">

              <div className="py-4 space-y-2">
                {navItems.map((item, index) =>
              <motion.button
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => scrollToSection(item.id, item.isHash)}
                className="block w-full text-left text-white hover:text-warm-coral-400 hover:bg-white/5 font-medium px-4 py-3 rounded-lg transition-colors">

                    {item.label}
                  </motion.button>
              )}
                <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="px-4 pt-2">

                  <Button
                  onClick={() => scrollToSection('services', true)}
                  className="w-full bg-gradient-to-r from-warm-coral-500 to-warm-coral-600 hover:from-warm-coral-600 hover:to-warm-coral-700 text-white py-3 rounded-full shadow-lg">

                    Get Started
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          }
        </AnimatePresence>
      </nav>
    </motion.header>);

};

export default Header;